import React, { useState, useCallback, useRef, useEffect } from "react"
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion"

const testimonials = [
  {
    quote: "The website is beautifully designed, which is great. The face of the website is very beautiful, I like very much. The dashboard is more easy and beautifully made. I like how simple and nice it looks. Very good work, thank you!, God Bless.",
    author: "Victor Sshasheetharan",
    role: "Proprietor",
    company: "VG Foods",
    avatar: "https://res.cloudinary.com/dwxijtvom/image/upload/v1774945780/testimonial_avatar_male_v2.png",
  }
]

function usePreloadImages(images: string[]) {
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image()
      img.src = src
    })
  }, [images])
}

function SplitText({ text }: { text: string }) {
  const words = text.split(" ")

  return (
    <span className="inline">
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 15, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{
            duration: 0.5,
            delay: i * 0.02,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="inline-block mr-[0.25em]"
        >
          {word}
        </motion.span>
      ))}
    </span>
  )
}

const Testimonials: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  usePreloadImages(testimonials.map((t) => t.avatar))

  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  const springConfig = { damping: 30, stiffness: 200 }
  const cursorX = useSpring(mouseX, springConfig)
  const cursorY = useSpring(mouseY, springConfig)

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!containerRef.current || isMobile) return
      const rect = containerRef.current.getBoundingClientRect()
      mouseX.set(e.clientX - rect.left)
      mouseY.set(e.clientY - rect.top)
    },
    [mouseX, mouseY, isMobile],
  )

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length)
  }

  const currentTestimonial = testimonials[activeIndex]

  return (
    <div
      ref={containerRef}
      className={`relative w-full min-h-[650px] md:min-h-[800px] flex items-center justify-center bg-black text-white py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden transition-all duration-500`}
      style={{ cursor: !isMobile && isHovered ? "none" : "default" }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleNext}
    >
      {/* Background Lighting */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-purple-600/30 rounded-full blur-[160px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-red-600/20 rounded-full blur-[160px]" />
      </div>

      {/* Magnetic Cursor - Laptop ONLY */}
      {!isMobile && (
        <motion.div
          className="pointer-events-none absolute z-50 mix-blend-difference"
          style={{
            x: cursorX,
            y: cursorY,
            translateX: "-50%",
            translateY: "-50%",
          }}
        >
          <motion.div
            className="rounded-full bg-white flex items-center justify-center shadow-2xl"
            animate={{
              width: isHovered ? 90 : 0,
              height: isHovered ? 90 : 0,
              opacity: isHovered ? 1 : 0,
            }}
            transition={{ type: "spring", damping: 25, stiffness: 250 }}
          >
            <motion.span
              className="text-black text-xs font-bold tracking-widest uppercase"
              animate={{ opacity: isHovered ? 1 : 0 }}
            >
              Next
            </motion.span>
          </motion.div>
        </motion.div>
      )}

      {/* Content Layout */}
      <div className="w-full max-w-4xl relative z-10 flex flex-col items-center md:items-start transition-all">
        
        {/* Top Interface: Avatars & Counter */}
        <div className="w-full flex flex-col md:flex-row items-center md:justify-between mb-16 md:mb-24 gap-8 md:gap-0">
          {/* Avatar previews */}
          <motion.div
            className="flex -space-x-3 items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-black overflow-hidden relative shadow-lg ${
                  i === activeIndex ? "ring-2 ring-[#FF4E45] ring-offset-2 ring-offset-black z-20 scale-110" : "grayscale opacity-40 z-10"
                }`}
                transition={{ duration: 0.3 }}
              >
                <img src={t.avatar} alt={t.author} className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </motion.div>

          {/* Counter */}
          <div className="flex items-baseline gap-2 font-mono order-first md:order-last">
            <AnimatePresence mode="wait">
              <motion.span
                key={activeIndex}
                className="text-3xl md:text-5xl font-light text-white"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
              >
                {String(activeIndex + 1).padStart(2, "0")}
              </motion.span>
            </AnimatePresence>
            <span className="text-white/20 text-xl font-light">/</span>
            <span className="text-white/20 text-xl font-light">{String(testimonials.length).padStart(2, "0")}</span>
          </div>
        </div>

        {/* The Quote */}
        <div className="w-full relative min-h-[120px] md:min-h-[200px] mb-12 md:mb-20">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={activeIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="text-2xl md:text-5xl font-light leading-snug md:leading-tight tracking-tight text-white text-center md:text-left"
            >
              <SplitText text={currentTestimonial.quote} />
            </motion.blockquote>
          </AnimatePresence>
        </div>

        {/* Author Panel */}
        <div className="w-full flex flex-col md:flex-row items-center md:items-end justify-between gap-12">
          <div className="flex items-center gap-6">
            <div className="relative w-16 h-16 md:w-20 md:h-20">
              <motion.div
                className="absolute -inset-2 rounded-full border border-[#FF4E45]/40"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 4 }}
              />
              {testimonials.map((t, i) => (
                <motion.img
                  key={t.author}
                  src={t.avatar}
                  alt={t.author}
                  className="absolute inset-0 w-full h-full rounded-full object-cover grayscale brightness-110 shadow-xl"
                  animate={{
                    opacity: i === activeIndex ? 1 : 0,
                    zIndex: i === activeIndex ? 1 : 0,
                    scale: i === activeIndex ? 1 : 0.8,
                  }}
                  transition={{ duration: 0.4 }}
                />
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                className="relative pl-6"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.4 }}
              >
                <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#FF4E45] rounded-full" />
                <h4 className="text-xl md:text-3xl font-normal text-white uppercase tracking-tighter">
                  {currentTestimonial.author}
                </h4>
                <p className="text-sm md:text-base text-white/30 font-mono tracking-widest mt-1">
                  {currentTestimonial.role} — {currentTestimonial.company}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress Indication (Mobile Optimized) */}
          <div className="w-full md:w-64">
            <div className="h-[2px] bg-white/10 rounded-full relative overflow-hidden">
              <motion.div
                className="absolute inset-y-0 left-0 bg-[#FF4E45]"
                initial={{ width: "0%" }}
                animate={{ width: `${((activeIndex + 1) / testimonials.length) * 100}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <div className="flex justify-between mt-3 text-[10px] text-white/20 uppercase tracking-[0.2em] font-bold">
              <span>Scroll to navigate</span>
              <span>{Math.round(((activeIndex + 1) / testimonials.length) * 100)}%</span>
            </div>
          </div>
        </div>

      </div>

      {/* Visual Accents */}
      <div className="absolute bottom-12 right-12 hidden md:flex items-center gap-3">
        <div className="w-3 h-3 rounded-full bg-[#FF4E45] animate-pulse" />
        <span className="text-[10px] text-white/20 font-bold tracking-[0.3em] uppercase">Studio Verification</span>
      </div>
    </div>
  )
}

export default Testimonials;